import { Link, useNavigate, useParams } from "react-router-dom";
import { ContenedorPageTemplate } from "../../../components/templates/ContenedorPageTemplate";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { getAllDocentes } from "../../../services/docente.service";
import { getAllGruposByIdDocenteIdIdioma } from "../../../services/grupo.service";

const nombresMeses = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

export const HistorialGrupoDocentePage = () => {
  const { idIdioma } = useParams();
  const [gruposDocente, setGruposDocente] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");
  const userName = useSelector((store) => store.usuario.username);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchGrupos = async () => {
      try {
        const docentesData = await getAllDocentes();
        const docente = docentesData.find(
          (est) => est.usuario.username === userName
        );

        if (docente) {
          const grupos = await getAllGruposByIdDocenteIdIdioma(
            docente.docente.idDocente,
            idIdioma
          );
          setGruposDocente(grupos);
        } else {
          setError("Docente no encontrado");
          setGruposDocente([]);
        }
      } catch (err) {
        setError("Error al cargar los grupos");
        console.error(err);
        setGruposDocente([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchGrupos();
  }, [userName, idIdioma]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(
      () => alert("Copiado al portapapeles"),
      () => alert("Error al copiar")
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "No definida";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("es-ES", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch (error) {
      return "Fecha inválida";
    }
  };

  const debeOcultarse = (grupo) => {
    if (!grupo?.fechaFin) return false;

    const hoy = new Date();
    const fechaFin = new Date(grupo.fechaFin);

    hoy.setHours(0, 0, 0, 0);
    fechaFin.setHours(0, 0, 0, 0);

    const fechaFinMas15Dias = new Date(fechaFin);
    fechaFinMas15Dias.setDate(fechaFinMas15Dias.getDate() + 15);

    return hoy > fechaFinMas15Dias;
  };

  const esGrupoFinalizado = (grupo) => {
    if (!grupo?.fechaFin) return false;

    const hoy = new Date();
    const fechaFin = new Date(grupo.fechaFin);

    hoy.setHours(0, 0, 0, 0);
    fechaFin.setHours(0, 0, 0, 0);

    return hoy > fechaFin;
  };

  const obtenerMesAnio = (fechaString) => {
    if (!fechaString) return null;
    try {
      const fecha = new Date(fechaString);
      const mes = fecha.getMonth();
      const anio = fecha.getFullYear();
      return { mes, anio, fechaCompleta: new Date(anio, mes, 1) };
    } catch {
      return null;
    }
  };

  const procesarGrupos = (grupos) => {
    return (
      grupos
        ?.map((grupo) => ({
          ...grupo,
          fechaReferencia:
            grupo.fechaInicio || grupo.creadoEn || grupo.fechaFin,
          mesAnio: obtenerMesAnio(
            grupo.fechaInicio || grupo.creadoEn || grupo.fechaFin
          ),
        }))
        ?.sort((a, b) => {
          const fechaA = new Date(a.fechaReferencia || 0);
          const fechaB = new Date(b.fechaReferencia || 0);
          return fechaB - fechaA;
        }) || []
    );
  };

  const agruparPorMes = (grupos) => {
    const gruposPorMes = grupos.reduce((acc, grupo) => {
      if (!grupo.mesAnio) {
        if (!acc["Sin fecha"]) {
          acc["Sin fecha"] = { grupos: [], fechaOrden: new Date(0) };
        }
        acc["Sin fecha"].grupos.push(grupo);
        return acc;
      }

      const clave = `${nombresMeses[grupo.mesAnio.mes]} ${grupo.mesAnio.anio}`;

      if (!acc[clave]) {
        acc[clave] = {
          grupos: [],
          fechaOrden: grupo.mesAnio.fechaCompleta,
        };
      }

      acc[clave].grupos.push(grupo);
      return acc;
    }, {});

    return Object.entries(gruposPorMes).sort(
      (a, b) => b[1].fechaOrden - a[1].fechaOrden
    );
  };

  const filtrarGruposPorFecha = (grupos) => {
    if (!fechaDesde && !fechaHasta) return grupos;

    return grupos.filter((grupo) => {
      if (!grupo.fechaFin) return true;

      const fechaFinGrupo = new Date(grupo.fechaFin);
      fechaFinGrupo.setHours(0, 0, 0, 0);

      let cumpleDesde = true;
      let cumpleHasta = true;

      if (fechaDesde) {
        const desde = new Date(fechaDesde);
        desde.setHours(0, 0, 0, 0);
        cumpleDesde = fechaFinGrupo >= desde;
      }

      if (fechaHasta) {
        const hasta = new Date(fechaHasta);
        hasta.setHours(23, 59, 59, 999);
        cumpleHasta = fechaFinGrupo <= hasta;
      }

      return cumpleDesde && cumpleHasta;
    });
  };

  const gruposAntiguos =
    gruposDocente?.filter((grupo) => debeOcultarse(grupo)) || [];
  const gruposAntiguosProcesados = procesarGrupos(gruposAntiguos);
  const gruposAntiguosFiltrados = filtrarGruposPorFecha(
    gruposAntiguosProcesados
  );
  const mesesAntiguosOrdenados = agruparPorMes(gruposAntiguosFiltrados);

  const renderizarGrupos = (grupos) => {
    return grupos.map((grupoDocente) => {
      const grupoFinalizado = esGrupoFinalizado(grupoDocente);

      return (
        <div
          key={grupoDocente?.idGrupo}
          className={`shadow-md rounded-lg p-6 flex flex-col mb-auto bg-white opacity-80 border border-gray-200`}
        >
          <h3
            className={`text-xl font-semibold text-center mb-2 ${
              grupoFinalizado ? "text-red-700" : "text-theme-blue"
            }`}
          >
            {grupoDocente?.nombre}
          </h3>

          {grupoFinalizado && (
            <div className="mb-4 bg-gradient-to-r from-red-600 to-red-700 text-white px-4 py-3 rounded-lg text-center">
              <div className="flex items-center justify-center gap-2 mb-1">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="font-bold text-sm">GRUPO FINALIZADO</span>
              </div>
              <p className="text-xs opacity-90">
                Fecha fin:{" "}
                {new Date(grupoDocente.fechaFin).toLocaleDateString("es-BO", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })}
              </p>
            </div>
          )}

          <div className="mb-4 text-sm">
            <p className="font-semibold">
              Módulo:{" "}
              <span className="font-normal truncate break-words whitespace-normal">
                {`${grupoDocente?.modulo?.nombre} / ${grupoDocente?.modulo?.sigla}`}
              </span>
            </p>
            <p className="font-semibold">
              Turno:{" "}
              <span className="font-normal truncate break-words whitespace-normal">
                {grupoDocente?.turno?.nombre}
              </span>
            </p>
            <p className="font-semibold">
              Modalidad:{" "}
              <span className="font-normal truncate break-words whitespace-normal">
                {grupoDocente?.modalidad?.nombre}
              </span>
            </p>
            <p className="font-semibold">
              Fecha de inicio:{" "}
              <span className="font-normal truncate break-words whitespace-normal">
                {formatDate(grupoDocente?.fechaInicio)}
              </span>
            </p>
            <p className="font-semibold">
              Fecha de fin:{" "}
              <span className="font-normal truncate break-words whitespace-normal">
                {formatDate(grupoDocente?.fechaFin)}
              </span>
            </p>
            <p className="font-semibold">
              Cuenta de zoom:{" "}
              {grupoDocente?.cuentaZoom ? (
                <span className="font-normal truncate break-words whitespace-normal">
                  <a
                    href={grupoDocente?.cuentaZoom}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline"
                  >
                    Link zoom
                  </a>
                  <button
                    onClick={() => copyToClipboard(grupoDocente?.cuentaZoom)}
                    className="ml-2 text-sm text-gray-600 hover:text-gray-800"
                  >
                    Copiar
                  </button>
                </span>
              ) : (
                <span className="font-normal">{"_"}</span>
              )}
            </p>
            <p className="font-semibold">
              Contraseña de zoom:{" "}
              {grupoDocente?.contrasenaZoom ? (
                <span className="font-normal truncate break-words whitespace-normal">
                  {grupoDocente?.contrasenaZoom}
                  <button
                    onClick={() => copyToClipboard(grupoDocente?.contrasenaZoom)}
                    className="ml-2 text-sm text-gray-600 hover:text-gray-800"
                  >
                    Copiar
                  </button>
                </span>
              ) : (
                <span className="font-normal">{"_"}</span>
              )}
            </p>
          </div>

          <details className="relative mb-4">
            <summary className="cursor-pointer font-semibold text-theme-blue hover:text-blue-700 w-fit">
              Horarios
            </summary>
            <div className="absolute left-0 w-full mt-2 bg-white border border-gray-300 p-2 rounded-md z-10 shadow-lg max-h-48 overflow-y-auto">
              {grupoDocente?.horarios?.length > 0 ? (
                <ul className="space-y-2">
                  {grupoDocente?.horarios?.map((horario, index) => (
                    <li
                      key={index}
                      className="truncate break-words whitespace-normal flex justify-between"
                    >
                      {`${horario?.horaInicio} - ${horario?.horaFin} - ${horario?.dia.nombre}`}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-center">No tiene horarios asignados.</p>
              )}
            </div>
          </details>

          <div className="mt-auto flex flex-col gap-2">
            <Link
              to={`/grupos/${grupoDocente.idGrupo}/registros/${idIdioma}/detalles`}
              state={{ grupoDocente }}
              className="bg-theme-yellow text-white font-semibold transition-colors hover:bg-yellow-500 py-2 px-4 rounded-lg shadow-md text-center"
            >
              Mis estudiantes
            </Link>

            <Link
              to={`/asistencia/iddocente/${grupoDocente.idDocente}/ididioma/${idIdioma}/idgrupo/${grupoDocente.idGrupo}`}
              state={{ grupoDocente }}
              className="bg-theme-yellow text-white font-semibold transition-colors hover:bg-yellow-500 py-2 px-4 rounded-lg shadow-md text-center"
            >
              Lista de Asistencia
            </Link>
          </div>
        </div>
      );
    });
  };

  return (
    <ContenedorPageTemplate objetos={gruposDocente} fondo={false}>
      <section className="mx-auto px-4 py-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <h2 className="text-3xl font-bold text-center md:text-left">
            Historial de Grupos
          </h2>

          <button
            onClick={() => navigate(`/idiomas/grupos/${idIdioma}`)}
            className="inline-flex items-center justify-center gap-2 self-center md:self-start bg-gray-200 text-gray-700 font-semibold py-3 px-6 rounded-xl shadow-lg hover:bg-gray-300 transition-colors"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            <span>Volver a mis grupos</span>
          </button>
        </div>

        {error && (
          <div className="mb-6 rounded-lg border border-red-300 bg-red-50 p-4 text-red-700">
            {error}
          </div>
        )}

        {isLoading ? (
          <p className="text-center text-gray-600">
            Cargando historial de grupos...
          </p>
        ) : gruposAntiguosProcesados.length > 0 ? (
          <div className="max-w-[1250px] mx-auto">
            <div className="mb-6 bg-white border-2 border-gray-300 rounded-lg p-6 shadow-md">
              <h3 className="text-xl font-bold text-gray-700 mb-4">
                Filtra por fechas de finalización
              </h3>
              <div className="flex flex-col sm:flex-row gap-4 mb-4">
                <div className="flex-1">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Fecha desde:
                  </label>
                  <input
                    type="date"
                    value={fechaDesde}
                    onChange={(e) => setFechaDesde(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-theme-blue"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Fecha hasta:
                  </label>
                  <input
                    type="date"
                    value={fechaHasta}
                    onChange={(e) => setFechaHasta(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-theme-blue"
                  />
                </div>
                <div className="flex items-end">
                  <button
                    onClick={() => {
                      setFechaDesde("");
                      setFechaHasta("");
                    }}
                    className="bg-gray-400 hover:bg-gray-500 text-white px-4 py-2 rounded-lg transition-colors font-semibold"
                  >
                    Limpiar
                  </button>
                </div>
              </div>

              <p className="text-sm text-gray-600">
                Mostrando {gruposAntiguosFiltrados.length} de{" "}
                {gruposAntiguosProcesados.length} grupos anteriores
              </p>
            </div>

            {mesesAntiguosOrdenados.length > 0 ? (
              mesesAntiguosOrdenados.map(([mesAnio, { grupos }]) => (
                <div key={mesAnio} className="mb-8">
                  <h4 className="text-xl font-semibold text-gray-500 mb-4 pb-2 border-b border-gray-300">
                    {mesAnio}
                  </h4>
                  <div className="grid grid-cols-[repeat(auto-fit,_360px)] justify-center gap-6">
                    {renderizarGrupos(grupos)}
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 bg-gray-50 rounded-lg">
                <p className="text-gray-600">
                  No hay grupos que coincidan con el rango de fechas
                  seleccionado.
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-16 bg-white rounded-2xl shadow">
            <p className="text-xl text-gray-600">
              Aún no tienes grupos en tu historial.
            </p>
          </div>
        )}
      </section>
    </ContenedorPageTemplate>
  );
};

export default HistorialGrupoDocentePage;



